import { IntegrationTypeV2 } from '@/gql/graphql';
import { getTimeZones } from '@vvo/tzdb';
import { formatTimezone } from './utils/format-utils';

// Type for selector items with consistent value/label pattern
export type SelectorItem = {
  value: string;
  label: string;
};

// List of US states using full names
// We use full state names as values since that's what our backend expects/returns
export const US_STATES = Object.freeze(
  [
    { value: 'Alabama', label: 'Alabama' },
    { value: 'Alaska', label: 'Alaska' },
    { value: 'Arizona', label: 'Arizona' },
    { value: 'Arkansas', label: 'Arkansas' },
    { value: 'California', label: 'California' },
    { value: 'Colorado', label: 'Colorado' },
    { value: 'Connecticut', label: 'Connecticut' },
    { value: 'Delaware', label: 'Delaware' },
    { value: 'District of Columbia', label: 'District of Columbia' },
    { value: 'Florida', label: 'Florida' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Hawaii', label: 'Hawaii' },
    { value: 'Idaho', label: 'Idaho' },
    { value: 'Illinois', label: 'Illinois' },
    { value: 'Indiana', label: 'Indiana' },
    { value: 'Iowa', label: 'Iowa' },
    { value: 'Kansas', label: 'Kansas' },
    { value: 'Kentucky', label: 'Kentucky' },
    { value: 'Louisiana', label: 'Louisiana' },
    { value: 'Maine', label: 'Maine' },
    { value: 'Maryland', label: 'Maryland' },
    { value: 'Massachusetts', label: 'Massachusetts' },
    { value: 'Michigan', label: 'Michigan' },
    { value: 'Minnesota', label: 'Minnesota' },
    { value: 'Mississippi', label: 'Mississippi' },
    { value: 'Missouri', label: 'Missouri' },
    { value: 'Montana', label: 'Montana' },
    { value: 'Nebraska', label: 'Nebraska' },
    { value: 'Nevada', label: 'Nevada' },
    { value: 'New Hampshire', label: 'New Hampshire' },
    { value: 'New Jersey', label: 'New Jersey' },
    { value: 'New Mexico', label: 'New Mexico' },
    { value: 'New York', label: 'New York' },
    { value: 'North Carolina', label: 'North Carolina' },
    { value: 'North Dakota', label: 'North Dakota' },
    { value: 'Ohio', label: 'Ohio' },
    { value: 'Oklahoma', label: 'Oklahoma' },
    { value: 'Oregon', label: 'Oregon' },
    { value: 'Pennsylvania', label: 'Pennsylvania' },
    { value: 'Rhode Island', label: 'Rhode Island' },
    { value: 'South Carolina', label: 'South Carolina' },
    { value: 'South Dakota', label: 'South Dakota' },
    { value: 'Tennessee', label: 'Tennessee' },
    { value: 'Texas', label: 'Texas' },
    { value: 'Utah', label: 'Utah' },
    { value: 'Vermont', label: 'Vermont' },
    { value: 'Virginia', label: 'Virginia' },
    { value: 'Washington', label: 'Washington' },
    { value: 'West Virginia', label: 'West Virginia' },
    { value: 'Wisconsin', label: 'Wisconsin' },
    { value: 'Wyoming', label: 'Wyoming' }
  ]
    .map((state) => Object.freeze(state))
    .sort((a, b) => a.label.localeCompare(b.label))
) as readonly SelectorItem[];

export const MERCHANT_CATEGORIES = Object.freeze(
  [
    { value: 'fashion', label: 'Fashion and Apparel' },
    { value: 'electronics', label: 'Electronics and Gadgets' },
    { value: 'food', label: 'Food and Beverage' },
    { value: 'health', label: 'Health and Wellness' },
    { value: 'home', label: 'Home and Garden' },
    { value: 'furniture', label: 'Furniture and Decor' },
    { value: 'office', label: 'Office Supplies' },
    { value: 'pet', label: 'Pet Supplies' },
    { value: 'beauty', label: 'Beauty and Personal Care' },
    { value: 'other', label: 'Other' }
  ]
    .map((category) => Object.freeze(category))
    .sort((a, b) => a.label.localeCompare(b.label))
);

// ISO 3166-1 standard country codes for values
export const COUNTRIES = Object.freeze([
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' }
]) as readonly SelectorItem[];

export const MEMBER_ROLE = Object.freeze([
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' }
]);

export const INTEGRATIONS_IN_BETA = Object.freeze([
  IntegrationTypeV2.Ga4,
  IntegrationTypeV2.Googleads
]);

// TIMEZONES is a deduplicated list of timezone options, filtered to ensure
// each formatted timezone identifier appears only once in the list
export const TIMEZONES = (() => {
  const seen = new Set();
  return (
    getTimeZones({ includeUtc: true })
      .filter((timezone) => {
        // Keep UTC
        if (timezone.name === 'UTC') return true;

        // Keep North American timezones
        return (
          timezone.continentName === 'North America' &&
          (timezone.countryCode === 'US' || timezone.countryCode === 'CA')
        );
      })
      .filter((timezone) => {
        // Deduplicate by formatted name (using existing format function)
        const identifier = formatTimezone(timezone);
        if (seen.has(identifier)) return false;
        seen.add(identifier);
        return true;
      })
      // Sort by offset
      .sort((a, b) => {
        // UTC first
        if (a.name === 'UTC') return -1;
        if (b.name === 'UTC') return 1;

        // Then by raw offset minutes
        return a.currentTimeOffsetInMinutes - b.currentTimeOffsetInMinutes;
      })
  );
})();
